import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrderSummary = ({ 
  subtotal, 
  tax, 
  shipping, 
  discount, 
  total, 
  onApplyPromoCode,
  onProceedToCheckout,
  isProcessing = false 
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(price);
  };

  const handleApplyPromo = () => {
    if (!promoCode?.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    // Mock promo code validation
    const validCodes = ['SAVE10', 'WELCOME20', 'BULK15'];
    if (validCodes?.includes(promoCode?.toUpperCase())) {
      setPromoError('');
      setPromoSuccess('Promo code applied successfully!');
      onApplyPromoCode(promoCode);
    } else {
      setPromoError('Invalid promo code');
      setPromoSuccess('');
    }
  };

  const summaryItems = [
    { label: 'Subtotal', value: subtotal, type: 'normal' },
    { label: 'Tax (18% VAT)', value: tax, type: 'normal' },
    { label: 'Shipping', value: shipping, type: 'normal' },
    ...(discount > 0 ? [{ label: 'Discount', value: -discount, type: 'discount' }] : []),
    { label: 'Total', value: total, type: 'total' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 sticky top-6">
      <h2 className="text-xl font-semibold text-foreground mb-6">Order Summary</h2>
      {/* Price Breakdown */}
      <div className="space-y-3 mb-6">
        {summaryItems?.map((item, index) => (
          <div 
            key={index}
            className={`flex justify-between items-center ${
              item?.type === 'total' ? 'pt-3 border-t border-border font-semibold text-lg' : ''
            }`}
          >
            <span className={`${
              item?.type === 'discount' ? 'text-success' : 
              item?.type === 'total' ? 'text-foreground' : 'text-muted-foreground'
            }`}>
              {item?.label}
            </span>
            <span className={`${
              item?.type === 'discount' ? 'text-success' : 
              item?.type === 'total' ? 'text-foreground font-semibold' : 'text-foreground'
            }`}>
              {formatPrice(item?.value)}
            </span>
          </div>
        ))}
      </div>
      {/* Promo Code Section */}
      <div className="mb-6">
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e?.target?.value)}
            className="flex-1"
            error={promoError}
          />
          <Button
            variant="outline"
            onClick={handleApplyPromo}
            disabled={!promoCode?.trim()}
          >
            Apply
          </Button>
        </div>
        {promoSuccess && (
          <div className="flex items-center gap-1 mt-2 text-sm text-success">
            <Icon name="CheckCircle" size={14} />
            <span>{promoSuccess}</span>
          </div>
        )}
      </div>
      {/* Security Badges */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Icon name="Shield" size={16} />
          <span>Secure Checkout</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="CreditCard" size={14} />
            <span>Cards</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="Smartphone" size={14} />
            <span>Mobile Money</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="Building" size={14} />
            <span>Bank Transfer</span>
          </div>
        </div>
      </div>
      {/* Checkout Button */}
      <Button
        variant="default"
        fullWidth
        onClick={onProceedToCheckout}
        loading={isProcessing}
        className="mb-4"
      >
        Proceed to Checkout
      </Button>
      {/* Continue Shopping */}
      <Button
        variant="outline"
        fullWidth
        onClick={() => window.location.href = '/product-catalog'}
      >
        Continue Shopping
      </Button>
      {/* Estimated Delivery */}
      <div className="mt-4 p-3 bg-muted rounded-lg">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Truck" size={16} className="text-primary" />
          <div>
            <p className="font-medium text-foreground">Estimated Delivery</p>
            <p className="text-muted-foreground">3-5 business days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;